import React, { useState, useRef, useEffect } from "react";
import { X, Send, Sparkles, Loader2, Trash2, Link2, WifiOff } from "lucide-react";
import { sendChat } from "../lib/api";
import { lsGet, lsSet, lsRemove } from "../lib/storage";

const SUGGESTIONS = [
    "What do I do if a passenger becomes unwell?",
    "How is the Car Scheme fare calculated?",
    "What should I never do as a volunteer?",
    "When do I need to submit my expense form?",
];

const LS_MESSAGES = "bscc.chat.messages.v1";
const LS_SESSION = "bscc.chat.session.v1";

export default function AIChatSheet({ open, onClose }) {
    const [messages, setMessages] = useState(() => lsGet(LS_MESSAGES, []));
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(() => lsGet(LS_SESSION, null));
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, loading]);

    // Persist messages & sessionId
    useEffect(() => { lsSet(LS_MESSAGES, messages); }, [messages]);
    useEffect(() => {
        if (sessionId) lsSet(LS_SESSION, sessionId);
    }, [sessionId]);

    const send = async (text) => {
        const content = (text ?? input).trim();
        if (!content || loading) return;
        const userId = `u-${Date.now()}`;
        setMessages(m => [...m, { id: userId, role: "user", content }]);
        setInput("");
        setLoading(true);
        try {
            const res = await sendChat(content, sessionId);
            setSessionId(res.session_id);
            setMessages(m => [...m, { id: `a-${Date.now()}`, role: "assistant", content: res.reply }]);
        } catch (e) {
            setMessages(m => [...m, { id: `e-${Date.now()}`, role: "assistant", content: "Sorry — I couldn't reach the assistant. Please try again, or call the BSCC Office on 01479 810004." }]);
        } finally {
            setLoading(false);
        }
    };

    const clearConversation = () => {
        if (messages.length > 0 && !window.confirm("Clear this conversation? Your messages will be removed from this device.")) return;
        setMessages([]);
        setSessionId(null);
        lsRemove(LS_MESSAGES);
        lsRemove(LS_SESSION);
    };

    const [shared, setShared] = useState(false);
    const copyShareLink = async () => {
        if (!sessionId) return;
        const url = `${window.location.origin}/?chat=${sessionId}`;
        try {
            await navigator.clipboard.writeText(url);
            setShared(true);
            setTimeout(() => setShared(false), 2000);
        } catch {
            window.prompt("Copy this link to resume the chat on another device:", url);
        }
    };

    const isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50" data-testid="ai-chat-sheet">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="absolute inset-y-0 right-0 w-full sm:w-[440px] bg-white shadow-2xl flex flex-col fade-up">
                <header className="flex items-center justify-between px-5 h-16 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-full bg-[#2C7A85] grid place-items-center text-white">
                            <Sparkles className="h-4 w-4" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-semibold text-gray-900" style={{ fontFamily: "Outfit" }}>Handbook Assistant</div>
                            <div className="text-xs text-gray-500">Trained on the BSCC Driver Handbook</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {sessionId && (
                            <button
                                onClick={copyShareLink}
                                className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-[#2C7A85] relative"
                                aria-label="Copy resume link"
                                data-testid="copy-resume-link"
                                title="Copy a link to continue this chat on another device"
                            >
                                <Link2 className="h-4 w-4" />
                                {shared && (
                                    <span className="absolute -bottom-7 right-0 bg-gray-900 text-white text-[11px] px-2 py-0.5 rounded whitespace-nowrap">Copied!</span>
                                )}
                            </button>
                        )}
                        {messages.length > 0 && (
                            <button
                                onClick={clearConversation}
                                className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-[#B91C1C]"
                                aria-label="Clear conversation"
                                data-testid="clear-conversation"
                                title="Clear conversation"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 -mr-2 rounded-md hover:bg-gray-100" aria-label="Close chat" data-testid="close-ai-chat">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </header>

                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                    {!isOnline && (
                        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 flex items-center gap-2 text-[13px] text-amber-900" data-testid="chat-offline">
                            <WifiOff className="h-4 w-4 flex-shrink-0" />
                            You're offline — the AI assistant needs an internet connection to answer.
                        </div>
                    )}
                    {messages.length === 0 && (
                        <div className="space-y-4">
                            <p className="text-gray-600 text-[15px] leading-relaxed">
                                Hello — I'm the BSCC Handbook Assistant. Ask me anything about journeys, policies, fares or emergencies.
                            </p>
                            <div className="space-y-2">
                                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Try asking</div>
                                {SUGGESTIONS.map(s => (
                                    <button
                                        key={s}
                                        onClick={() => send(s)}
                                        className="block w-full text-left text-[14px] px-3 py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors"
                                        data-testid={`chat-suggestion-${s.slice(0, 20)}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {messages.map((m) => (
                        <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.role}`}>
                            <div className={`max-w-[88%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed whitespace-pre-wrap ${
                                m.role === "user"
                                    ? "bg-[#2C7A85] text-white rounded-br-sm"
                                    : "bg-gray-50 border border-gray-200 text-gray-800 rounded-bl-sm"
                            }`}>
                                {m.content}
                            </div>
                        </div>
                    ))}

                    {loading && (
                        <div className="flex justify-start">
                            <div className="px-4 py-3 rounded-2xl bg-gray-50 border border-gray-200 text-gray-500 text-sm flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Thinking…
                            </div>
                        </div>
                    )}
                </div>

                <form
                    onSubmit={(e) => { e.preventDefault(); send(); }}
                    className="border-t border-gray-200 p-3 flex items-end gap-2 bg-white"
                >
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Ask a question…"
                        rows={1}
                        className="flex-1 resize-none max-h-32 px-3 py-2.5 border border-gray-300 rounded-md text-[15px] focus:ring-2 focus:ring-[#2C7A85] focus:border-transparent outline-none"
                        data-testid="chat-input"
                    />
                    <button
                        type="submit"
                        disabled={loading || !input.trim()}
                        className="h-11 w-11 rounded-md bg-[#2C7A85] hover:bg-[#1F5F69] disabled:opacity-50 text-white grid place-items-center"
                        data-testid="chat-send"
                    >
                        <Send className="h-4 w-4" />
                    </button>
                </form>
            </div>
        </div>
    );
}
